<script lang="ts" setup>
import AUApi from "@/api";

import { ref, reactive, onMounted, watch } from "vue";
import {
  getDisplay,
  getEinheitFromColumn,
  getKZColumn,
  getKZColumnsByPrefix,
  getValue,
  roundNumber,
  getKZValueDisplay,
} from "@/DataHelper";

import { ChartType, getAlternateColorChart } from "../ChartTypesAndFunctions";
import {
  ChartBaseParameters,
  ChartBaseProperties,
  ChartBaseUiData,
  initChartBase,
  refreshChartData,
  showDialogChart,
} from "./ChartBaseStatic";
// import { printVueElement, showSnackbar } from "@/UIHelper";
import DashboardChartsZuordnungenMenu from "@/components/dashboard/Charts/ZuordnungenMenu.vue";
import DashboardChartsSmallButtonBar from "@/components/dashboard/Charts/SmallButtonBar.vue";

import { BarChart, DoughnutChart, useDoughnutChart } from "vue-chart-3";
import {
  Chart,
  registerables,
  ChartData,
  BarController,
  DoughnutController,
  Tooltip,
  ChartOptions,
} from "chart.js";
import { begriffBezeichnung } from "@/local";
Chart.register(...registerables);
Chart.register(BarController, DoughnutController, Tooltip);

declare interface ChartEntry {
  aic: number;
  options: ChartOptions;
  data: ChartData;
  name?: string;
  height: number;
}

declare interface CChartUIData extends ChartBaseUiData {
  chartType: string;
  noData: boolean;

  minColumn?: DataColumn;
  maxColumn?: DataColumn;
  periodisch?: boolean;
}

const props = defineProps<ChartBaseProperties>();
const emit = defineEmits<{
  back: [];
}>();

const pageParams: ChartBaseParameters = {
  zeitbereich: AUApi.Current.zeitbereich,
  query: props.params.query,
  refreshing: false,
  bewegungsdaten: undefined,
};
const { ui, params } = initChartBase<CChartUIData, ChartBaseParameters>(
  props,
  {},
  {},
);

declare interface ChartisSettings {
  legendTitles: string[];
  seriesColumnKZ: string;
  labelsColumnKZ: string;
  chartType: ChartType;
  chartData: ChartEntry[];
  unit: string;
  minColor: string; // = "orange";
  maxColor: string; // = "red";
  min: number; // = 0;
  max: number; // = 10000;
}

const uiData = reactive<CChartUIData>(ui);

const chartSettings = reactive<ChartisSettings>({
  legendTitles: [],
  seriesColumnKZ: "series",
  labelsColumnKZ: "labelBez",
  chartType: props.params.chartType ?? "Bar",
  chartData: [],
  unit: "",
  minColor: "orange",
  maxColor: "red",
  min: 0,
  max: 10000,
});

let barChartData: any[];
let lblsColumn: any;

onMounted(async () => {
  refresh();
});
async function refresh() {
  await refreshChartData(uiData, props, params);
  dataLoaded(params.queryData);
}

function removeDuplicates(arr: any[]) {
  return arr.filter((item, index) => arr.indexOf(item) === index);
}

function dataLoaded(data: QueryResponse) {
  try {
    if ((data?.data?.length ?? 0) === 0) {
      uiData.noData = true;
      return;
    }
    uiData.noData = false;
    // props.params.queryData = params.lastQueryResponse = data;
    const seriesColumns = getKZColumnsByPrefix(
      chartSettings.seriesColumnKZ,
      data.columns,
    ) as DataColumn[];
    const aicColumn = getKZColumn("aic", data.columns);
    if (!aicColumn) {
      console.warn(
        "CChart: keine Spalte mit kz='aic' gefunden - alle Zeilen fallen auf aic=-1 und landen dadurch im selben Chart!",
        { query: props.params.query, chartType: chartSettings.chartType, columns: data.columns },
      );
    }
    const header = data.header;
    if (header.abfJahr) {
      uiData.periodisch = true;
    }
    if (uiData.periodisch) {
      const allseries = seriesColumns.filter((c) =>
        c.meta.kz?.startsWith("series"),
      );
      const kzs = removeDuplicates(allseries.map((s) => s.meta.kz));
      const lbls = kzs
        .map((s) => allseries.find((a) => a.meta.kz === s))
        .map((s) => s?.title ?? "???");
      chartSettings.legendTitles = lbls;
    } else {
      // Nicht periodisch: Titel pro Serie merken
      chartSettings.legendTitles = seriesColumns?.map((s) => s.title) ?? [];
    }

    lblsColumn =
      getKZColumn(chartSettings.labelsColumnKZ, data.columns) ??
      getKZColumn("label", data.columns);

    if ((seriesColumns?.length ?? 0) > 0) {
      const e = seriesColumns[0].meta.einheit;
      chartSettings.unit = " " + getEinheitFromColumn(seriesColumns[0]).kennung;
    }
    const defaultHeight = uiData.size ?? 300;
    const chartData: ChartEntry[] = [];

    setMinMax(data);
    const allowedAICs =
      Array.isArray(props.QvAICs) && props.QvAICs.length > 0
        ? new Set(props.QvAICs)
        : null; // null = keine Einschränkung (z. B. QuickView leer)

    data.data.forEach((row, rix) => {
      const aic = aicColumn ? getValue(row, aicColumn) : -1;
      if (allowedAICs && !allowedAICs.has(aic)) {
        return; // diesen Datensatz überspringen
      }
      let chart = chartData.find((c) => c.aic === aic);
      if (!chart) {
        chart = {
          aic,
          height: defaultHeight,
          data: {
            labels: [],
            datasets: [
              { label: "-", data: [] },
              { label: "-", data: [] },
            ],
          },
          options: {
            indexAxis: "y",
            scales: {
              x: { stacked: false },
              y: { stacked: false },
            },
            plugins: {
              legend: {
                // Pie-Legende beim ersten Render sichtbar
                display:
                  chartSettings.chartType === "Pie" ||
                  (props.QvAICs?.length ?? 0) == 0,
                position: "top",
              },
            },
          },
        };
        chartData.push(chart);

        // Labels initialisieren
        if (seriesColumns?.length === 1 && lblsColumn) {
          if (!chart?.aic || chart.aic === -1) {
            chart.data.labels = data.data.map((r) => {
              let lbl: any = getDisplay(r, lblsColumn);
              if (typeof lbl === "object") lbl = lbl.bezeichnung;
              return lbl ?? "-";
            });
          }
        } else {
          if (uiData.periodisch) {
            const series1 = seriesColumns.filter((c) => c.meta.kz === "series");
            chart.data.labels =
              series1?.map((s) => s.titlePeriodisch ?? s.title) ?? [];
          } else {
            chart.data.labels = seriesColumns?.map((s) => s.title) ?? [];
          }
        }

        // WICHTIG: Beim ersten Erstellen die Dataset-Labels setzen
        if (!uiData.periodisch && chart && chart.data) {
          seriesColumns?.forEach((s, ix) => {
            if (chart!.data.datasets[ix]) {
              chart!.data.datasets[ix].label = s.title;
            }
          });
        }
      }

      if (uiData.periodisch) {
        // Jahreschart
        const val: any = [];
        const val1: any = [];
        if (aicColumn?.meta?.kz) {
          chart.options.plugins!.title = {
            text: getKZValueDisplay(row, aicColumn.meta.kz, data.columns),
            display: true,
          };
        }
        let name = "";
        let name1 = "";
        for (let i = 0; i < seriesColumns.length; i++) {
          const s = seriesColumns[i];
          const value = row[s.name];
          if (s.meta.kz === "series") {
            name = s.title;
            val.push(value);
          } else if (s.meta.kz === "series1") {
            name1 = s.title;
            val1.push(value);
          }
        }
        chart.data.datasets[0].data.push(val);
        chart.data.datasets[0].label = name;
        chart.data.datasets[1].data.push(val1);
        chart.data.datasets[1].label = name1;
      } else if (chartSettings.chartType === "BalkenStackedMinMax") {
        const s = seriesColumns[0];
        checkVal4metaData(row, s, chart.aic, "", chart);
        chart.data.datasets[0].label = s.title;
        if (chart.data.datasets[1]) {
          chart.data.datasets[1].label = s.title;
        }
      } else {
        for (let ix = 0; ix < seriesColumns.length; ix++) {
          const s = seriesColumns[ix];
          checkVal4metaData(row, s, chart.aic, "", chart);
          // Pie: Label pro Dataset sicherstellen
          if (chartSettings.chartType === "Pie" && chart.data?.datasets?.[ix]) {
            chart.data.datasets[ix].label = s.title;
          }
        }
      }
    });

    // Für AICs aus QvAICs, die in data.data KEINE einzige Zeile hatten (zB weil im
    // gewählten Zeitraum nichts gebucht wurde), entsteht oben nie ein Chart-Eintrag - die
    // Liste zeigt dann nur die AICs mit Buchungen statt aller. Platzhalter ergänzen, damit
    // isChartEmpty() für sie greift und "keine Daten gefunden" angezeigt wird.
    if (allowedAICs && props.QvAICs) {
      const existingAICs = new Set(chartData.map((c) => c.aic));
      props.QvAICs.forEach((aic) => {
        if (existingAICs.has(aic)) {
          return;
        }
        chartData.push({
          aic,
          name: String(aic),
          height: defaultHeight,
          data: {
            labels: [],
            datasets: [
              { label: "-", data: [] },
              { label: "-", data: [] },
            ],
          },
          options: {
            plugins: { legend: { display: false } },
          },
        });
      });
    }

    if (
      chartSettings.chartType === "Bar" ||
      chartSettings.chartType === "BalkenStackedMinMax"
    ) {
      chartData.forEach((c) => {
        c.options.indexAxis = "y";
        if (
          props.params.chartOrientation &&
          props.params.chartOrientation === "vertical"
        ) {
          c.options.indexAxis = "x";
        } else if (!props.QuickViewMode && props.QvAICs?.length === 0) {
          // if (c.data && c.data.length > 0) {
          //   c.height = c.data.length * 15;
          // }
          if (c.data.labels && c.data.labels.length > 0) {
            c.height = c.data.labels?.length * 30;
          } else {
            c.height = data.data.length * 15;
          }
        }
        c.options.scales = {
          x: {
            stacked: chartSettings.chartType !== "BalkenStackedMinMax",
          },
          y: {
            stacked: chartSettings.chartType === "BalkenStackedMinMax",
          },
        };
        if (!uiData.periodisch) {
          if (chartSettings.chartType === "BalkenStackedMinMax") {
            c.options.plugins = {
              legend: {
                display: false,
                position: "top", // "top" : "bottom",
              },
              title: {
                display: false,
                text: "Hier könnte ihr Titel stehen",
              },
            };
            // höhe anpassen sonst nicht lesbar
            c.height = data.data.length * 30;
          } else {
            c.options.plugins = {
              legend: {
                display: (props.QvAICs?.length ?? 0) == 0,
                position: "top", // "top" : "bottom",
              },
              title: {
                display: false,
                text: "Hier könnte ihr Titel stehen",
              },
            };
          }
        }
      });
    }

    // alle leeren datasets entfernen
    chartData.forEach((c) => {
      c.data.datasets = c.data.datasets.filter((d) => d.data.length > 0);
    });

    // Nach dem Befüllen: Legend-Display für Pie sicherstellen
    chartData.forEach((c) => {
      c.options.plugins = c.options.plugins || {};
      c.options.plugins.legend = c.options.plugins.legend || {};
      if (chartSettings.chartType === "Pie") {
        c.options.plugins.legend.display = true;
        c.options.plugins.legend.position = "top";
      }
    });

    // Reihenfolge an QvAICs (Projekt-/MA-Liste) ausrichten statt an der Reihenfolge, in der
    // die Buchungszeilen zufällig zuerst auftauchen - sonst landet zB "Luttenberger" (aic
    // 119309, Listenposition 24) an Position 0, nur weil seine erste Buchung die erste Zeile
    // in data.data war.
    if (allowedAICs && props.QvAICs) {
      const orderIndex = new Map(props.QvAICs.map((aic, ix) => [aic, ix]));
      chartData.sort(
        (a, b) =>
          (orderIndex.get(a.aic) ?? Number.MAX_SAFE_INTEGER) -
          (orderIndex.get(b.aic) ?? Number.MAX_SAFE_INTEGER),
      );
    }

    chartSettings.chartData = [...chartData];
    // data.data war nicht leer, aber nach dem QvAICs-Filter (Zeile ~170) kann trotzdem kein
    // einziger Chart übrig bleiben - ohne diese Prüfung bleibt uiData.noData auf false und
    // weder ein Chart noch der "keine Daten"-Hinweis wird angezeigt (leere v-list).
    if (chartSettings.chartData.length === 0) {
      uiData.noData = true;
    }
    barChartData = chartSettings.chartData.map((c) =>
      useDoughnutChart({ chartData: <any>c.data, options: c.options }),
    );
  } finally {
    uiData.showLoading = false;
  }
}

function isChartEmpty(chart: ChartEntry): boolean {
  const datasets = chart?.data?.datasets ?? [];
  return !datasets.some((d: any) =>
    (d.data ?? []).some((v: any) => typeof v === "number" && v !== 0),
  );
}

function setMinMax(data: QueryResponse) {
  const minCol = getKZColumn("min", data.columns);
  const maxCol = getKZColumn("max", data.columns);

  chartSettings.minColor = fixColorNames(
    minCol?.meta?.farbe ?? chartSettings.minColor,
  );
  chartSettings.maxColor = fixColorNames(
    maxCol?.meta?.farbe ?? chartSettings.maxColor,
  );

  uiData.minColumn = minCol;
  uiData.maxColumn = maxCol;
}

// STMM: bitte checken dass ihr im backend schon englische farben schickt :D
function fixColorNames(name: string) {
  if (name === "rot") {
    return "red";
  }
  if (name === "grün") {
    return "green";
  }
  if (name === "blau") {
    return "blue";
  }
  if (name === "gelb") {
    return "yellow";
  }
  return name;
}
const fallbackColors = ["#4CAF50", "#2196F3", "#FF9800", "#9C27B0"];
function checkVal4metaData(
  row: QueryDataRow,
  s: DataColumn,
  aic: number,
  tooltip: string,
  chart: ChartEntry,
): number[] {
  const vals: number[] = [];
  const value = getValue(row, s);
  const valKZ = getDisplay(row, s);
  const label = getDisplay(row, lblsColumn);
  let defaultColor = s.meta.farbe;

  // alternative farben nur bei PIE!
  if (!defaultColor && chartSettings.chartType === "Pie") {
    const idx = chart.data.datasets[0].data.length % fallbackColors.length;
    defaultColor = fallbackColors[idx];
  } else if (!defaultColor) {
    defaultColor = "green";
  }
  let min = chartSettings.min;
  if (uiData.minColumn) {
    min = getValue(row, uiData.minColumn) ?? chartSettings.min;
  }
  let max = chartSettings.max;
  if (uiData.maxColumn) {
    max = getValue(row, uiData.maxColumn) ?? chartSettings.max;
    max = roundNumber(max, 2);
  }
  // TODO: Tooltip in Pie aus label befüllen
  if (!tooltip && chartSettings.chartType === "BalkenStackedMinMax") {
    tooltip = valKZ + " (" + min + "/" + max + ")";
  }
  //console.log(value);
  // maximum auf bar schauen

  if (!chart.data.datasets[0].backgroundColor)
    chart.data.datasets[0].backgroundColor = [];
  if (!chart.data.datasets[1].backgroundColor)
    chart.data.datasets[1].backgroundColor = [];
  const backgroundColors = chart.data.datasets[0].backgroundColor as string[];
  const backgroundColors1 = chart.data.datasets[1].backgroundColor as string[];
  if (value > max && chartSettings.chartType === "BalkenStackedMinMax") {
    chart.data.datasets[0].data.push(max);
    chart.data.datasets[1].data.push(value - max);

    backgroundColors.push(defaultColor ?? "green");
    backgroundColors1.push(chartSettings.maxColor);
  } else if (value < min && chartSettings.chartType === "BalkenStackedMinMax") {
    if (min >= 0) {
      // wenn min >=0 dann nur einen gelben balken
      chart.data.datasets[0].data.push(0);
      chart.data.datasets[1].data.push(value - min);

      backgroundColors.push(defaultColor ?? "green");
      backgroundColors1.push(chartSettings.minColor);
    } else {
      // wenn min < 0 dann min grün lassen...
      chart.data.datasets[0].data.push(min);
      chart.data.datasets[1].data.push(value + min);

      backgroundColors.push(defaultColor ?? "green");
      backgroundColors1.push(chartSettings.minColor);
    }
    // vals.push({ value: value - min, meta: { aic, value, ix, cix, color: this.minColor } });
  } else {
    if (chartSettings.chartType === "BalkenStackedMinMax") {
      chart.data.datasets[0].data.push(value);
      chart.data.datasets[1].data.push(0);

      backgroundColors.push(defaultColor ?? "green");
      backgroundColors1.push(chartSettings.minColor);
    } else if (value > max && chartSettings.chartType === "Bar") {
      chart.data.datasets[0].data.push(value);
      backgroundColors.push(chartSettings.maxColor);
    } else if (chartSettings.chartType === "Bar") {
      chart.data.datasets[0].data.push(value);
      backgroundColors.push(defaultColor ?? "green");
    } else {
      chart.data.datasets[0].data.push(value);
      if (!chart.data.labels) {
        chart.data.labels = [];
      }
      // Bei mehreren seriesColumns (ein Slice pro Spalte/Zeile) sind die Labels beim
      // Chart-Anlegen schon korrekt mit den Spaltentiteln vorbefüllt (siehe
      // "Labels initialisieren" oben) - ein zusätzliches Push hier würde pro Slice ein
      // weiteres (falsches) Label anhängen und labels/data auseinanderlaufen lassen. Nur im
      // Langformat-Fall (1 seriesColumn + lblsColumn, Labels starten leer) sollen die Labels
      // hier zeilenweise mitwachsen - erkennbar daran, dass labels noch nicht so lang ist
      // wie data.
      if (chart.data.labels.length < chart.data.datasets[0].data.length) {
        chart.data.labels.push(label);
      }
      backgroundColors.push(defaultColor ?? "green");
    }
  }
  return vals;
}

function logData() {
  console.log(chartSettings.chartType);
  console.log(chartSettings.chartData);
}
function printData() {
  alert("Druck dieses Charts derzeit nicht implementiert!");
  /*const chartist = this.$refs.chartist as any[];

  const svg = chartist[0].$el?.children[0];
  if (svg.tagName.toLowerCase() === "svg") {
    printVueElement(
      svg as HTMLElement,
      this.title ?? this.params?.queryData?.header?.bezeichnung
    );
    return;
  } else {
    showSnackbar({
      text: "Druck dieses Charts derzeit nicht implementiert!",
      duration: 3000,
      color: "red",
    });
  }*/
}
function QvAICsChanged() {
  refresh();
}

function getChartStyle(cix: number, chart?: ChartEntry) {
  if (props.QuickViewMode || uiData.size) {
    // if (this.orientation === "vertical") {
    return {
      height: uiData.size + "px",
      background: getAlternateColorChart(cix),
      overflow: "hidden",
      padding: 0,
    };
  } else {
    return {
      width: uiData.size + "px",
      background: getAlternateColorChart(cix),
      overflow: "hidden",
    };
  }
  return { background: getAlternateColorChart(cix) };
}
</script>
<template>
  <v-card class="pa-0" flat min-width="100%" :loading="uiData.showLoading">
    <v-card-text class="pa-0">
      <DashboardChartsSmallButtonBar
        :showPrint="false"
        @refresh="refresh"
        @info="logData()"
        @print="printData()"
      />

      <v-list
        v-if="!uiData.showLoading && !uiData.noData"
        lines="three"
        tile
        class="pa-0"
      >
        <v-list-item
          v-for="(chart, cix) in chartSettings.chartData"
          :key="'chart' + cix"
          :style="{
            padding: '0 0 0 0',
            background: getAlternateColorChart(cix),
            height: chart.height + 'px',
            width: '100%',
          }"
        >
          <v-list-item-subtitle v-if="uiData.periodisch">{{
            chart.name
          }}</v-list-item-subtitle>
          <!-- chart hier :D -->
          <v-card
            class="d-flex gap-3"
            :style="{ background: getAlternateColorChart(cix) }"
          >
            <div class="flex-grow-1">
              <BarChart
                v-if="
                  chartSettings.chartType === 'Bar' ||
                  chartSettings.chartType === 'BalkenStackedMinMax'
                "
                :height="chart.height"
                :chartData="<any>chart.data"
                :options="chart.options"
              />
              <DoughnutChart
                v-if="chartSettings.chartType === 'Pie' && !isChartEmpty(chart)"
                :chartData="<any>chart.data"
                :height="chart.height"
                :options="chart.options"
              />
              <div
                v-if="chartSettings.chartType === 'Pie' && isChartEmpty(chart)"
                :style="{ height: chart.height + 'px' }"
                class="d-flex align-center justify-center"
              >
                {{
                  begriffBezeichnung(
                    "Web_keineDaten_gefunden",
                    "keine Daten gefunden",
                  )
                }}
              </div>
            </div>

            <DashboardChartsZuordnungenMenu
              v-if="uiData.zuordnungen && uiData.zuordnungen.length > 0"
              :zuordnungen="uiData.zuordnungen"
              :aic="chart.aic"
              :showButtons="true"
              :color="'black'"
              :noSelect="true"
              :zeitbereich="pageParams.zeitbereich"
              style="min-width: 20px; max-width: 20px; flex-shrink: 0"
            />
          </v-card>
        </v-list-item>
      </v-list>
      <div v-if="uiData.noData">
        <div
          v-for="(aic, cix) in QvAICs"
          :key="'c' + cix"
          :style="getChartStyle(cix)"
          @click="
            showDialogChart(
              uiData,
              props,
              params.zeitbereich ?? AUApi.Current.zeitbereich,
              undefined,
              aic,
            )
          "
        >
          {{
            begriffBezeichnung(
              "Web_keineDaten_gefunden",
              "keine Daten gefunden",
            )
          }}
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>
<style>
.ct-series-a .ct-bar {
  stroke-width: 12;
}

.ct-label {
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ct-chart-bar.ct-horizontal-bars .ct-label.ct-vertical.ct-start {
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: end;
}
</style>
